import type { ReactNode } from "react";
import { HANDOVER_NOTES_MAX_CHARS, type RepositoryView } from "@baton/contracts";
import type { HandoffDraftState } from "./compose.js";

/**
 * The structured half of a handoff: subject and acceptance criteria as before, then the
 * handover lists (one item per line), notes, and where the work lives. The composer's own
 * body field is passed as `children` so the reading order stays subject, brief, lists.
 */
export function HandoffFields(props: {
  value: HandoffDraftState;
  onChange(next: HandoffDraftState): void;
  repositories: RepositoryView[];
  /** The wire composer picks the repository here; the chat drawer already has a routing select and hides this one. */
  showRepositoryPicker: boolean;
  children?: ReactNode;
  compact?: boolean;
}) {
  const value = props.value;
  const rows = props.compact ? 2 : 3;
  const repository = props.repositories.find((candidate) => candidate.id === value.repositoryId) ?? null;
  const set = <K extends keyof HandoffDraftState>(key: K) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    props.onChange({ ...value, [key]: event.target.value });
  const pickRepository = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const picked = props.repositories.find((candidate) => candidate.id === event.target.value) ?? null;
    props.onChange({ ...value, repositoryId: picked?.id ?? null, branch: picked?.defaultBranch ?? "" });
  };
  return <div className={`handoff-fields${props.compact ? " compact" : ""}`}>
    <label className="span-2">Subject<input value={value.subject} onChange={set("subject")} maxLength={240} placeholder="What to deliver" /></label>
    {props.children && <div className="span-2">{props.children}</div>}
    <label className="span-2">Acceptance criteria<textarea value={value.criteria} onChange={set("criteria")} rows={rows} placeholder={"One per line: tests pass\nno push"} /></label>
    <label>Done<textarea value={value.done} onChange={set("done")} rows={rows} placeholder="One item per line" /></label>
    <label>To do<textarea value={value.todo} onChange={set("todo")} rows={rows} placeholder="One item per line" /></label>
    <label>Verify<textarea value={value.verify} onChange={set("verify")} rows={rows} placeholder="One step per line" /></label>
    <label>Ideas<textarea value={value.ideas} onChange={set("ideas")} rows={rows} placeholder="One item per line" /></label>
    <label className="span-2">Notes<textarea value={value.notes} onChange={set("notes")} rows={rows} maxLength={HANDOVER_NOTES_MAX_CHARS} placeholder="Anything the next agent should know" /></label>
    {props.showRepositoryPicker && <label>Repository
      <select value={value.repositoryId ?? ""} onChange={pickRepository}>
        <option value="">No repository</option>
        {props.repositories.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.ownerHandle}/{candidate.name}</option>)}
      </select>
    </label>}
    {repository && <label>Branch<input value={value.branch} onChange={set("branch")} maxLength={255} placeholder={repository.defaultBranch} /></label>}
  </div>;
}
